import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { InsightMarkdownPipe } from '@frontend/shared';
import { TranslocoModule, provideTranslocoScope } from '@jsverse/transloco';
import { finalize } from 'rxjs/operators';
import { reloadOnLocaleChange } from '../../language';
import { LoaderGlobeAnimationDetailComponent } from '../../loaders';
import { BreadcrumbService } from '../../navigation/breadcrumb/breadcrumb.service';
import { PageTitleService } from '../../navigation/title/title.service';
import { WindowService } from '../../utilities';
import { ShowBannerComponent } from '../show-banner/show-banner.component';
import { ShowPlayerComponent } from '../show-player/show-player.component';
import { ShowsCreditsComponent } from '../shows-credits/shows-credits.component';
import { ShowsLikeButtonComponent } from '../shows-like-button/shows-like-button.component';
import { CHANNEL_NAMES, otherLinks, playerLink } from '../shows-player';
import { ShowsTranslationFollower } from '../shows-translation';
import { Clip, DEFAULT_SHOWS_DEFINITION, clipPath, Show, ShowsDefinition, ShowsItemRef } from '../shows.model';
import { ShowsService } from '../shows.service';

/**
 * One clip — a trailer, a highlight, a promotion or a tool demonstration, with a way to the
 * episode it belongs to. An episode's clip is at `/shows/{show}/{episode}/clips/{clip}`, any
 * other at `/shows/{show}/clips/{clip}` (`clipPath`); a clip reached at the other address is
 * moved to its own, so older links keep working.
 */
@Component({
  selector: 'multisite-clip-page',
  imports: [DatePipe, RouterModule, TranslocoModule, InsightMarkdownPipe, LoaderGlobeAnimationDetailComponent, ShowBannerComponent, ShowPlayerComponent, ShowsCreditsComponent, ShowsLikeButtonComponent],
  providers: [provideTranslocoScope('shows', 'tools')],
  template: `
    <div *transloco="let t">
      @if (show(); as currentShow) {
        <multisite-show-banner [show]="currentShow" [linkTo]="[definition.showsPath, currentShow.slug]"></multisite-show-banner>
      }

      <div class="container">
        @let current = clip();

        @if (current) {
          <div class="row">
            <div class="col-lg-8">
              <p class="text-muted mb-1 mt-2">
                <span>{{ t('shows.clip_kind.' + current.kind) }}</span>
                <span class="mx-1">·</span>
                <span>{{ current.publishedAt | date: 'longDate' }}</span>
              </p>
              <h1 class="font-weight-bold text-break">{{ current.title }}</h1>

              @if (translation.translating()) {
                <div class="d-flex align-items-center my-2" role="status">
                  <multisite-loader-globe-animation-detail [width]="40" [height]="40"></multisite-loader-globe-animation-detail>
                  <span class="ml-2">{{ t('news.translating') }}</span>
                </div>
              }

              @if (player(); as link) {
                <div class="my-3"><multisite-show-player [link]="link" [title]="current.title"></multisite-show-player></div>
              }

              @if (alsoOn().length) {
                <p class="small mb-3">
                  <span class="text-muted mr-1">{{ t('shows.other_platforms') }}</span>
                  @for (link of alsoOn(); track link.url) {
                    <a class="mr-2" [href]="link.url" target="_blank" rel="noopener">{{ channelNames[link.channel] }}</a>
                  }
                </p>
              }

              @if (current.summary) {
                <div class="text-break" [innerHTML]="current.summary | insightMarkdown"></div>
              }

              @if (episodeSlug(); as episode) {
                <a class="btn btn-outline-primary btn-sm my-3" [routerLink]="[definition.showsPath, current.show, episode]">{{ t('shows.full_episode') }}</a>
              }
            </div>

            <div class="col-lg-4">
              <div class="sticky-top pt-3">
                <div class="mb-3"><multisite-shows-like-button [item]="ref()"></multisite-shows-like-button></div>
                <div class="mb-4"><multisite-shows-credits [crew]="current.crew"></multisite-shows-credits></div>
                @if (current.tools.length) {
                  <p class="h5">{{ t('tools.tools') }}</p>
                  <ul class="list-unstyled">
                    @for (tool of current.tools; track tool.id) {
                      <li><a [routerLink]="[definition.toolsPath, tool.slug]">{{ tool.name }}</a></li>
                    }
                  </ul>
                }
              </div>
            </div>
          </div>
        }

        @if (!current && loading()) {
          <div class="my-5"><multisite-loader-globe-animation-detail [width]="100" [height]="100"></multisite-loader-globe-animation-detail></div>
        }

        @if (notFound()) {
          <div class="text-center my-5">
            <p>{{ t('common.not_found') }}</p>
            <a [routerLink]="[definition.showsPath]">{{ t('shows.all_shows') }}</a>
          </div>
        }

        @if (failed()) {
          <p class="text-danger my-5 text-center">{{ t('error.could_not_load') }}</p>
        }
      </div>
    </div>
  `,
})
export class ClipPageComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private showsService = inject(ShowsService);
  private breadcrumbService = inject(BreadcrumbService);
  private pageTitleService = inject(PageTitleService);
  private windowService = inject(WindowService);

  readonly definition: ShowsDefinition = this.route.snapshot.data['showsSection'] ?? DEFAULT_SHOWS_DEFINITION;
  readonly channelNames = CHANNEL_NAMES;
  readonly translation = new ShowsTranslationFollower<Clip>();

  clip = signal<Clip | null>(null);
  show = signal<Show | null>(null);
  loading = signal(false);
  notFound = signal(false);
  failed = signal(false);

  readonly showSlug = signal('');
  /** The episode the address names, if any. */
  readonly addressEpisode = signal<string | null>(null);
  readonly clipSlug = signal('');
  readonly ref = computed<ShowsItemRef>(() => ({ kind: 'clip', show: this.showSlug(), slug: this.clipSlug() }));
  readonly player = computed(() => playerLink(this.clip()?.channelLinks ?? []));
  readonly alsoOn = computed(() => otherLinks(this.clip()?.channelLinks ?? []));

  /** The episode the clip belongs to; a clip of a show or season has none. */
  readonly episodeSlug = computed(() => {
    const owner = this.clip()?.owner;

    return owner?.type === 'episodes' ? owner.slug : null;
  });

  constructor() {
    this.route.paramMap.subscribe((params) => {
      this.showSlug.set(params.get('show') ?? '');
      this.addressEpisode.set(params.get('episode'));
      this.clipSlug.set(params.get('clip') ?? '');
      this.windowService.goToTop();
      this.load();
    });
    reloadOnLocaleChange(() => this.load());
  }

  private load(): void {
    this.translation.stop();
    this.loading.set(true);
    this.notFound.set(false);
    this.failed.set(false);

    this.showsService
      .getClip(this.showSlug(), this.clipSlug())
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (clip) => {
          if (!this.atOwnAddress(clip)) {
            this.router.navigate(clipPath(this.definition, clip), { replaceUrl: true });

            return;
          }

          this.present(clip);
          this.translation.follow(this.ref(), clip, (translated) =>
            this.present({ ...translated, crew: clip.crew, tools: clip.tools, images: clip.images }),
          );
        },
        error: (error) => (error?.status === 404 ? this.notFound.set(true) : this.failed.set(true)),
      });

    this.showsService.getShow(this.showSlug()).subscribe({
      next: (show) => {
        this.show.set(show);
        this.breadcrumbService.setBreadcrumbFragment({ urlFragment: 'show', fragmentName: show.name });
      },
      error: () => this.show.set(null),
    });

    const episode = this.addressEpisode();

    if (episode) {
      this.showsService.getEpisodeTitle(this.showSlug(), episode).subscribe({
        next: (title) => this.breadcrumbService.setBreadcrumbFragment({ urlFragment: 'episode', fragmentName: title }),
        error: () => undefined,
      });
    }
  }

  /** Whether the address names the episode the clip belongs to, or none when it belongs to none. */
  private atOwnAddress(clip: Clip): boolean {
    const episode = clip.owner?.type === 'episodes' ? clip.owner.slug : null;

    return episode === this.addressEpisode();
  }

  private present(clip: Clip): void {
    this.clip.set(clip);
    this.pageTitleService.setTitle(clip.title);
  }
}
